import React from "react";
import { Card, CardBody, Badge, Button, Col } from "reactstrap";
import { Edit, Trash2, DollarSign, Percent, Calendar, Package, Users, Tag } from "lucide-react";
import ReactMarkdown from 'react-markdown';

interface ProductCardProps {
  product: any;
  typeLabel: string;
  showActions?: boolean;
  onEdit?: (product: any) => void;
  onDelete?: (productId: number) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  isLocked?: boolean;
  subscriptionPeriods?: Array<{ value: string; label: string }>;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  typeLabel,
  showActions = false,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp = false,
  canMoveDown = false,
  isLocked = false,
  subscriptionPeriods = [],
}) => {
  // Helper function to clean markdown content
  const cleanMarkdown = (text: string | null | undefined): string => {
    if (!text) return '';
    // Convert to string
    let cleaned = String(text);
    
    // Replace HTML entities if any (do this first)
    cleaned = cleaned.replace(/&amp;/g, '&')
                     .replace(/&lt;/g, '<')
                     .replace(/&gt;/g, '>')
                     .replace(/&quot;/g, '"')
                     .replace(/&#39;/g, "'")
                     .replace(/&ast;/g, '*');
    
    // Preserve newlines that are immediately before markdown syntax (**, *, __, etc.)
    // We use placeholders to protect them from being converted later
    // First, save double newlines (paragraph breaks)
    cleaned = cleaned.replace(/\n\n/g, '{{PARAGRAPH_BREAK}}');
    
    // Then, save newlines before markdown syntax using a placeholder
    // Pattern: newline followed by optional whitespace and markdown syntax
    cleaned = cleaned.replace(/\n(?=\s*[#*_`\-\[\]])/g, '{{MARKDOWN_NEWLINE}}');
    
    // Now convert remaining single newlines to markdown line breaks (two spaces + newline)
    cleaned = cleaned.replace(/\n/g, '  \n');
    
    // Restore the preserved newlines
    cleaned = cleaned.replace(/{{PARAGRAPH_BREAK}}/g, '\n\n');  // Restore double newlines
    cleaned = cleaned.replace(/{{MARKDOWN_NEWLINE}}/g, '\n');   // Restore newlines before markdown
    
    // Fix trailing spaces in markdown syntax (e.g., "**text **" -> "**text**")
    // This handles bold (**text **), italic (*text *), and other markdown patterns
    cleaned = cleaned.replace(/\*\*([^*]+?)\s+\*\*/g, '**$1**')  // Bold with trailing space
                     .replace(/\*([^*]+?)\s+\*/g, '*$1*')        // Italic with trailing space
                     .replace(/__([^_]+?)\s+__/g, '__$1__')       // Bold underscore with trailing space
                     .replace(/_([^_]+?)\s+_/g, '_$1_')          // Italic underscore with trailing space
                     .replace(/~~([^~]+?)\s+~~/g, '~~$1~~');      // Strikethrough with trailing space
    
    return cleaned;
  };

  const getSubscriptionPeriodLabel = (period: string) => {
    if (!period) return "Once";
    const found = subscriptionPeriods.find((sp) => sp.value === period);
    return found ? found.label : period === "month" ? "Monthly" : period === "year" ? "Yearly" : "Once";
  };

  return (
    <Col md={3} className="mb-3">
      <Card
        className="h-100 border shadow-sm"
        style={{
          borderRadius: "8px",
        }}
      >
        <CardBody className="p-4">
          {/* Header Section */}
          <div className="d-flex justify-content-between align-items-start mb-3 pb-3 border-bottom">
            <div className="flex-grow-1">
              <h6 className="mb-2 fw-bold" style={{ fontSize: "1rem", color: "#212529" }}>
                {product.title}
              </h6>
              <Badge color="primary" className="px-2 py-1" style={{ fontSize: "0.75rem" }}>
                {typeLabel}
              </Badge>
            </div>
            {showActions && !isLocked && (
              <div className="d-flex gap-1">
                {onMoveUp && (
                  <Button
                    color="link"
                    size="sm"
                    onClick={onMoveUp}
                    disabled={!canMoveUp}
                    title="Move up"
                    className="p-1 d-inline-flex align-items-center justify-content-center"
                    style={{ 
                      width: "28px", 
                      height: "28px", 
                      color: canMoveUp ? '#1976d2' : '#ccc',
                      textDecoration: 'none'
                    }}
                  >
                    <i className="bx bx-chevron-up" style={{fontSize: 16}}></i>
                  </Button>
                )}
                {onMoveDown && (
                  <Button
                    color="link"
                    size="sm"
                    onClick={onMoveDown}
                    disabled={!canMoveDown}
                    title="Move down"
                    className="p-1 d-inline-flex align-items-center justify-content-center"
                    style={{ 
                      width: "28px", 
                      height: "28px", 
                      color: canMoveDown ? '#1976d2' : '#ccc',
                      textDecoration: 'none'
                    }}
                  >
                    <i className="bx bx-chevron-down" style={{fontSize: 16}}></i>
                  </Button>
                )}
                <Button
                  color="info"
                  size="sm"
                  onClick={() => onEdit && onEdit(product)}
                  title="Edit"
                  className="p-1 d-inline-flex align-items-center justify-content-center"
                  style={{ width: "28px", height: "28px" }}
                >
                  <Edit size={14} />
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => product.id && onDelete && onDelete(product.id)}
                  title="Delete"
                  className="p-1 d-inline-flex align-items-center justify-content-center"
                  style={{ width: "28px", height: "28px" }}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            )}
          </div>

          {/* Description */}
          {product.description && (
            <div className="mb-3">
              <div className="text-muted mb-0" style={{ fontSize: "0.875rem", lineHeight: "1.5" }}>
                <ReactMarkdown
                    components={{
                        p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: "0.875rem", color: '#6c757d' }}>{children}</p>,
                        strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                        em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                        ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: "0.875rem", color: '#6c757d' }}>{children}</ul>,
                        ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: "0.875rem", color: '#6c757d' }}>{children}</ol>,
                        li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: "0.875rem", color: '#6c757d' }}>{children}</li>,
                        a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                    }}
                >
                    {cleanMarkdown(product.description)}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* Key Information Grid */}
          <div className="mb-3">
            {/* Price */}
            <div
              className="d-flex align-items-center mb-2 p-2 rounded"
              style={{ background: "#f8fafc" }}
            >
              <DollarSign size={16} className="text-primary me-2" style={{ flexShrink: 0 }} />
              <div className="flex-grow-1">
                <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 500 }}>
                  Price
                </div>
                <div
                  className="fw-bold"
                  style={{ fontSize: "0.9rem", color: "#1e293b" }}
                >
                  {product.is_fixed_price
                    ? `$${product.price?.toFixed(2) || "0.00"}`
                    : "Contact for Pricing"}
                </div>
              </div>
            </div>

            {/* Share Percentage */}
            <div
              className="d-flex align-items-center mb-2 p-2 rounded"
              style={{ background: "#f0fdf4" }}
            >
              <Percent size={16} className="text-success me-2" style={{ flexShrink: 0 }} />
              <div className="flex-grow-1">
                <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 500 }}>
                  Share
                </div>
                <div className="d-flex align-items-baseline gap-2">
                  <span
                    className="fw-bold"
                    style={{ fontSize: "0.9rem", color: "#1e293b" }}
                  >
                    {product.share_percentage}%
                  </span>
                  {product.price && product.share_percentage > 0 && (
                    <span
                      className="text-success fw-semibold"
                      style={{ fontSize: "0.8rem" }}
                    >
                      ${((product.price * product.share_percentage) / 100).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Cancellation Days */}
            <div
              className="d-flex align-items-center mb-2 p-2 rounded"
              style={{ background: "#fef3c7" }}
            >
              <Calendar size={16} className="text-warning me-2" style={{ flexShrink: 0 }} />
              <div className="flex-grow-1">
                <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 500 }}>
                  Cancellation
                </div>
                <div
                  className="fw-semibold"
                  style={{ fontSize: "0.85rem", color: "#1e293b" }}
                >
                  {product.cancellation_days} days
                </div>
              </div>
            </div>

            {/* Subscription Period */}
            {(product.product_type === "subscriptionaddon" ||
              product.product_type === "plan" ||
              product.product_type === "planoptionals") && (
              <div
                className="d-flex align-items-center mb-2 p-2 rounded"
                style={{ background: "#eff6ff" }}
              >
                <Package size={16} className="text-info me-2" style={{ flexShrink: 0 }} />
                <div className="flex-grow-1">
                  <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 500 }}>
                    Period
                  </div>
                  <div
                    className="fw-semibold"
                    style={{ fontSize: "0.85rem", color: "#1e293b" }}
                  >
                    {getSubscriptionPeriodLabel(product.subscription_period)}
                  </div>
                </div>
              </div>
            )}

            {/* Quantifiable */}
            {(product.product_type === "addon" || product.product_type === "onetimeproduct") && (
              <div
                className="d-flex align-items-center mb-2 p-2 rounded"
                style={{ background: "#f5f3ff" }}
              >
                <Users size={16} className="text-purple me-2" style={{ flexShrink: 0 }} />
                <div className="flex-grow-1">
                  <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 500 }}>
                    Quantifiable
                  </div>
                  <div
                    className="fw-semibold"
                    style={{ fontSize: "0.85rem", color: "#1e293b" }}
                  >
                    {product.quantifiable ? "Yes" : "No"}
                  </div>
                </div>
              </div>
            )}

            {/* Group */}
            {product.product_type === "planoptionals" && (
              <div
                className="d-flex align-items-center mb-2 p-2 rounded"
                style={{ background: "#fef2f2" }}
              >
                <Tag size={16} className="text-danger me-2" style={{ flexShrink: 0 }} />
                <div className="flex-grow-1">
                  <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 500 }}>
                    Group
                  </div>
                  <div
                    className="fw-semibold"
                    style={{ fontSize: "0.85rem", color: "#1e293b" }}
                  >
                    {product.group || "N/A"}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Features Section */}
          {product.features && product.features.length > 0 && (
            <div className="pt-3 border-top">
              <div className="mb-2" style={{ fontSize: "0.75rem", color: "#6c757d", fontWeight: 600 }}>
                Features
              </div>
              <div className="d-flex flex-wrap gap-1">
                {product.features.map((feature: string, fIdx: number) => (
                  <Badge
                    key={fIdx}
                    color="light"
                    className="px-2 py-1"
                    style={{
                      fontSize: "0.75rem",
                      color: "#495057",
                      border: "1px solid #dee2e6",
                      fontWeight: 400,
                    }}
                  >
                    {feature}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default ProductCard;
